import { ChatgptVariantFieldType } from "@redotech/cockroachdb-codegen/tables/product/chatgpt-variant-field-type";
import { DEFAULT_CURRENCY } from "@redotech/money/util";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  MeasurementSchema,
  MoneySchema,
} from "../attributes";
import { BaseProductListingSchema, ListingType } from "../base-listing";
import { ChatGptListingMediaSchema } from "./chatgpt-media";

export enum SizeSystem {
  US = "US",
  UK = "UK",
  EU = "EU",
  AU = "AU",
  BR = "BR",
  CN = "CN",
  DE = "DE",
  FR = "FR",
  IT = "IT",
  JP = "JP",
  MEX = "MEX",
}
export const SizeSystemSchema = createEnum(SizeSystem);

export enum Gender {
  Male = "male",
  Female = "female",
  Unisex = "unisex",
}
export const GenderSchema = createEnum(Gender);

export const ChatGptVariantListingSchema_ = BaseProductListingSchema.extend({
  listingType: z.literal(ListingType.ChatGpt),
  id: z.bigint(),
  sku: z.string().nullable().default(""),
  price: MoneySchema.nullable().default({
    amount: 0,
    currency: DEFAULT_CURRENCY,
  }),
  salePrice: MoneySchema.nullable(),
  salePriceEffectiveDate: z.date().nullable(),
  unitPricingMeasure: MeasurementSchema.nullable(),
  baseMeasure: MeasurementSchema.nullable(),
  pricingTrend: z.string().nullable(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  sizeSystem: SizeSystemSchema.nullable(),
  gender: GenderSchema.nullable(),
  offerId: z.string().nullable(),
  customVariant1Category: z.string().nullable(),
  customVariant1Value: z.string().nullable(),
  customVariant2Category: z.string().nullable(),
  customVariant2Value: z.string().nullable(),
  customVariant3Category: z.string().nullable(),
  customVariant3Value: z.string().nullable(),
  media: z.array(ChatGptListingMediaSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ChatGptVariantListingSchema: z.ZodType<ChatGptVariantListing> =
  ChatGptVariantListingSchema_;
export interface ChatGptVariantListing extends z.infer<
  typeof ChatGptVariantListingSchema_
> {}

export const ChatGptVariantFieldTypeSchema = createEnum(
  ChatgptVariantFieldType,
);
const ChatGptVariantMappingSchema_ = z.object({
  fieldName: ChatGptVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ChatGptVariantMappingSchema: z.ZodType<ChatGptVariantMapping> =
  ChatGptVariantMappingSchema_;
export interface ChatGptVariantMapping extends z.infer<
  typeof ChatGptVariantMappingSchema_
> {}

export const ChatGptVariantAttributeType = {
  [ChatgptVariantFieldType.Sku]: AttributeType.ShortText,
  [ChatgptVariantFieldType.Price]: AttributeType.Money,
  [ChatgptVariantFieldType.SalePrice]: AttributeType.Money,
  [ChatgptVariantFieldType.SalePriceEffectiveDate]: AttributeType.Date,
  [ChatgptVariantFieldType.UnitPricingMeasure]: AttributeType.Dimension,
  [ChatgptVariantFieldType.BaseMeasure]: AttributeType.Dimension,
  [ChatgptVariantFieldType.PricingTrend]: AttributeType.ShortText,
  [ChatgptVariantFieldType.Color]: AttributeType.Color,
  [ChatgptVariantFieldType.Size]: AttributeType.ShortText,
  [ChatgptVariantFieldType.SizeSystem]: AttributeType.Enum,
  [ChatgptVariantFieldType.Gender]: AttributeType.Enum,
  [ChatgptVariantFieldType.OfferId]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant1Category]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant1Value]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant2Category]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant2Value]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant3Category]: AttributeType.ShortText,
  [ChatgptVariantFieldType.CustomVariant3Value]: AttributeType.ShortText,
} satisfies Record<ChatgptVariantFieldType, AttributeType>;

export const ChatGptVariantAttributeSchema = {
  [ChatgptVariantFieldType.Sku]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.Sku]
    ].nullable(),
  [ChatgptVariantFieldType.Price]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.Price]
    ].nullable(),
  [ChatgptVariantFieldType.SalePrice]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.SalePrice]
    ].nullable(),
  [ChatgptVariantFieldType.SalePriceEffectiveDate]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatgptVariantFieldType.SalePriceEffectiveDate
      ]
    ].nullable(),
  [ChatgptVariantFieldType.UnitPricingMeasure]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.UnitPricingMeasure]
    ].nullable(),
  [ChatgptVariantFieldType.BaseMeasure]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.BaseMeasure]
    ].nullable(),
  [ChatgptVariantFieldType.PricingTrend]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.PricingTrend]
    ].nullable(),
  [ChatgptVariantFieldType.Color]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.Color]
    ].nullable(),
  [ChatgptVariantFieldType.Size]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.Size]
    ].nullable(),
  [ChatgptVariantFieldType.SizeSystem]:
    createSafeNullableEnumSchema(SizeSystemSchema),
  [ChatgptVariantFieldType.Gender]: createSafeNullableEnumSchema(GenderSchema),
  [ChatgptVariantFieldType.OfferId]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.OfferId]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant1Category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatgptVariantFieldType.CustomVariant1Category
      ]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant1Value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.CustomVariant1Value]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant2Category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatgptVariantFieldType.CustomVariant2Category
      ]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant2Value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.CustomVariant2Value]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant3Category]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[
        ChatgptVariantFieldType.CustomVariant3Category
      ]
    ].nullable(),
  [ChatgptVariantFieldType.CustomVariant3Value]:
    AttributeValueSchema[
      ChatGptVariantAttributeType[ChatgptVariantFieldType.CustomVariant3Value]
    ].nullable(),
};

export type ChatGptVariantAttributeValueType = {
  [key in ChatgptVariantFieldType]: (typeof ChatGptVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getChatGptVariantAttributeValue<
  T extends ChatgptVariantFieldType,
>(field: T, value: unknown): ChatGptVariantAttributeValueType[T] | null {
  return ChatGptVariantAttributeSchema[field].parse(
    value,
  ) as ChatGptVariantAttributeValueType[T];
}

export function getChatGptVariantAttributeType<
  T extends ChatgptVariantFieldType,
>(field: T): (typeof ChatGptVariantAttributeType)[T] {
  return ChatGptVariantAttributeType[field];
}

export function getChatGptVariantListingAttributeValue<
  T extends ChatgptVariantFieldType,
>({
  listing,
  field,
}: {
  listing: ChatGptVariantListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "sku":
      fieldValue = listing.sku;
      break;
    case "price":
      fieldValue = listing.price;
      break;
    case "sale_price":
      fieldValue = listing.salePrice;
      break;
    case "sale_price_effective_date":
      fieldValue = listing.salePriceEffectiveDate;
      break;
    case "unit_pricing_measure":
      fieldValue = listing.unitPricingMeasure;
      break;
    case "base_measure":
      fieldValue = listing.baseMeasure;
      break;
    case "pricing_trend":
      fieldValue = listing.pricingTrend;
      break;
    case "color":
      fieldValue = listing.color;
      break;
    case "size":
      fieldValue = listing.size;
      break;
    case "size_system":
      fieldValue = listing.sizeSystem;
      break;
    case "gender":
      fieldValue = listing.gender;
      break;
    case "offer_id":
      fieldValue = listing.offerId;
      break;
    case "custom_variant1_category":
      fieldValue = listing.customVariant1Category;
      break;
    case "custom_variant1_value":
      fieldValue = listing.customVariant1Value;
      break;
    case "custom_variant2_category":
      fieldValue = listing.customVariant2Category;
      break;
    case "custom_variant2_value":
      fieldValue = listing.customVariant2Value;
      break;
    case "custom_variant3_category":
      fieldValue = listing.customVariant3Category;
      break;
    case "custom_variant3_value":
      fieldValue = listing.customVariant3Value;
      break;
    default:
      assertNever(field);
  }
  return ChatGptVariantAttributeSchema[field].parse(fieldValue);
}
